import { useId, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { FieldContext } from './field-context';

/**
 * Label + control + hint + error, wired for accessibility.
 *
 *   <FormField label="Username" error={errors.username?.message} required>
 *     <Input {...register('username')} />
 *   </FormField>
 *
 * Any Input / Select / Textarea / NumberInput / MoneyInput inside picks up `id`, `aria-describedby`,
 * `aria-invalid` and `aria-required` from context. For Checkbox/Switch use their own `label` prop and
 * FormField with `label` omitted (only error/hint are shown).
 */
export function FormField({
  label,
  error,
  hint,
  required,
  children,
  className,
}: {
  label?: ReactNode;
  error?: string | null;
  hint?: ReactNode;
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <FieldContext.Provider value={{ id, describedBy, invalid: !!error, required: !!required }}>
      <div className={cn('flex flex-col gap-1.5', className)}>
        {label && (
          <label htmlFor={id} className="text-sm font-medium text-fg">
            {label}
            {required && (
              <span aria-hidden className="ml-0.5 text-danger">
                *
              </span>
            )}
          </label>
        )}
        {children}
        {hint && !error && (
          <p id={hintId} className="text-xs text-fg-subtle">
            {hint}
          </p>
        )}
        {error && (
          <p id={errorId} role="alert" className="text-xs font-medium text-danger">
            {error}
          </p>
        )}
      </div>
    </FieldContext.Provider>
  );
}
